import { AnimatePresence, motion } from "framer-motion";
import { FC, PropsWithChildren, useState } from "react";
import styled, { css } from "styled-components";

import Text from "@/components/Text";

const Title = styled.div`
  padding: 24px 0;
  cursor: pointer;
  border-top: 1px solid #cecece;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Accordion = styled.div`
  &:last-child {
    border-bottom: 1px solid #cecece;
  }
`;

const Content = styled(motion.div)``;

const Button = styled.div<{ $isOpen: boolean }>`
  border: 1px solid #cecece;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-left: 20px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.purpleLighter};
  transition: all ease-out 0.2s;
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      color: ${({ theme }) => theme.colors.white};
      background: ${({ theme }) => theme.colors.purpleLighter};
      border-color: ${({ theme }) => theme.colors.purpleLighter}; ;
    `}
`;

const ContentInner = styled.div`
  padding: 8px 0 32px;
  strong {
    font-weight: 700;
  }
  ${Text} {
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  a {
    color: ${({ theme }) => theme.colors.purpleLighter};
    transition: opacity ease-out 0.2s;
    &:hover {
      opacity: 0.5;
    }
  }
`;

interface Props {
  title: string;
}

const HomeAccordion: FC<PropsWithChildren<Props>> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Accordion>
      <Title onClick={() => setIsOpen((prev) => !prev)}>
        <Text $type="heading2">{title}</Text>
        <Button $isOpen={isOpen}>
          {isOpen ? (
            <svg
              width="12"
              height="3"
              viewBox="0 0 12 3"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 1.13721C0 0.584922 0.447715 0.137207 1 0.137207H11.3354C11.8877 0.137207 12.3354 0.584922 12.3354 1.13721C12.3354 1.68949 11.8877 2.13721 11.3354 2.13721H1C0.447715 2.13721 0 1.68949 0 1.13721Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.16797 1C7.16797 0.447715 6.72025 0 6.16797 0C5.61568 0 5.16797 0.447715 5.16797 1V5.13721H1C0.447715 5.13721 0 5.58492 0 6.13721C0 6.68949 0.447715 7.13721 1 7.13721H5.16797V11.2742C5.16797 11.8265 5.61568 12.2742 6.16797 12.2742C6.72025 12.2742 7.16797 11.8265 7.16797 11.2742V7.13721H11.3354C11.8877 7.13721 12.3354 6.68949 12.3354 6.13721C12.3354 5.58492 11.8877 5.13721 11.3354 5.13721H7.16797V1Z"
                fill="currentColor"
              />
            </svg>
          )}
        </Button>
      </Title>
      <AnimatePresence>
        {isOpen && (
          <Content
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                opacity: 1,
                height: "auto",
                transition: {
                  ease: "easeOut",
                  duration: 0.2,
                },
              },
              collapsed: {
                opacity: 0,
                height: 0,
                transition: {
                  ease: "easeOut",
                  duration: 0.2,
                  opacity: {
                    duration: 0.1,
                  },
                },
              },
            }}
          >
            <ContentInner>{children}</ContentInner>
          </Content>
        )}
      </AnimatePresence>
    </Accordion>
  );
};

export default HomeAccordion;
